import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import ConversationConfiguration from './ConversationConfiguration';
import SurveyGenerator from '@/components/SurveyGenerator';
import { AppLayout } from '@/components/layout/AppLayout';
import { AppHeader } from '@/components/layout/AppHeader';
import { Footer } from '@/components/layout/Footer';
import { useTranslation } from 'react-i18next';
import { isUUID } from '@/lib/slugUtils';

// Survey Configuration component for tools with tool_type "survey"
const SurveyConfiguration: React.FC = () => {
  const navigate = useNavigate();
  const { toolId: toolParam } = useParams<{ toolId: string }>();
  const { lang } = useParams<{ lang?: string }>();
  const prefix = `/${lang || 'en'}`;
  const [tool, setTool] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchTool = async () => {
      if (!toolParam) return;
      
      try {
        const isId = isUUID(toolParam);
        let query = supabase
          .from('tools')
          .select('id, slug, name, description, icon')
          .eq('is_active', true);
          
        if (isId) {
          query = query.eq('id', toolParam);
        } else {
          query = query.eq('slug', toolParam);
        }
        
        const { data, error } = await query.single();

        if (error) throw error;
        setTool(data);
      } catch (error) {
        console.error('Error fetching tool:', error);
        navigate(prefix);
      } finally {
        setLoading(false);
      }
    };

    fetchTool();
  }, [toolParam, navigate]);

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin mx-auto w-8 h-8 border-2 border-primary border-t-transparent rounded-full mb-4"></div>
            <p className="text-muted-foreground">{t('tool.config.loading')}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <AppHeader
        title={tool?.name ? t('tool.config.title', { toolName: tool.name }) : t('tool.config.titleFallback')}
        subtitle={t('tool.config.subtitle')}
        breadcrumbs={[
          { label: tool?.name || 'Tool', href: `/${(useParams<{ lang?: string }>().lang || 'en')}/tools/${tool?.slug || tool?.id}` },
          { label: t('tool.config.breadcrumb') }
        ]}
      />
      <div className="container mx-auto px-4 py-12">
        <SurveyGenerator />
      </div>
      <Footer />
    </AppLayout>
  );
};

// Main router component
const ToolConfiguration: React.FC = () => {
  const { toolId: toolParam } = useParams<{ toolId: string }>();
  const { lang } = useParams<{ lang?: string }>();
  const prefix = `/${lang || 'en'}`;
  const [toolType, setToolType] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchToolType = async () => {
      if (!toolParam) return;
      
      try {
        const isId = isUUID(toolParam);
        let query = supabase
          .from('tools')
          .select(`
            id,
            tool_type:tool_types(name)
          `)
          .eq('is_active', true);
          
        if (isId) {
          query = query.eq('id', toolParam);
        } else {
          query = query.eq('slug', toolParam);
        }
        
        const { data, error } = await query.single();

        if (error) throw error;
        setToolType(data.tool_type?.name || 'survey');
      } catch (error) {
        console.error('Error fetching tool type:', error);
        navigate(prefix);
      } finally {
        setLoading(false);
      }
    };

    fetchToolType();
  }, [toolParam, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin mx-auto w-8 h-8 border-2 border-primary border-t-transparent rounded-full mb-4"></div>
          <p className="text-muted-foreground">{t('tool.config.loading')}</p>
        </div>
      </div>
    );
  }

  if (toolType === 'conversation') {
    return <ConversationConfiguration />;
  }

  return <SurveyConfiguration />;
};

export default ToolConfiguration;